import log from "loglevel";
import ProjectWorld, { createServerEvent, ProjectEventType, type ProjectUser } from "../ProjectWorld";
import type { BaseEvent, BaseProjectionResult, BaseEventInput, EventProcessor } from "@utilitydelta/clientside-event-sourcing";
import { ServerEvent } from "@utilitydelta/eventplanedb-client";

export const MINOR_VERSION: number = 1;

export interface AssignRandomUsername_LocalEvent extends BaseEvent<ProjectEventType> {
    tp: typeof ProjectEventType.AssignRandomUsername;
    userId?: string; //ID of the user assigning to
    clientId: string; //ID of the client assigning to
    randomUsername: string; // Randomly generated username
}

export interface AssignRandomUsername_ProjectionResult extends BaseProjectionResult {
    kind: 'AssignRandomUsername_ProjectionResult';
    projectUser: ProjectUser; // User record
}

export interface AssignRandomUsername_EventInputs extends BaseEventInput {
    kind: 'AssignRandomUsername_EventInputs';
    userId?: string; //ID of the user assigning to
    clientId: string; //ID of the client assigning to
    randomUsername: string; // Randomly generated username
}

export type AssignRandomUsername_EventProcessorType = EventProcessor<ProjectEventType, AssignRandomUsername_EventInputs, AssignRandomUsername_LocalEvent, AssignRandomUsername_ProjectionResult, AssignRandomUsername_LocalEvent, ServerEvent<ProjectEventType>>;

export class AssignRandomUsername_EventProcessor implements AssignRandomUsername_EventProcessorType {

    private projectWorld: ProjectWorld;
    constructor(pw: ProjectWorld) { this.projectWorld = pw; }

    public get shouldEncrypt(): boolean { return true; }
    public get eventType(): ProjectEventType { return ProjectEventType.AssignRandomUsername; }
    public get eventTypeMinorVersion(): number { return MINOR_VERSION; }
    public get undoEventType(): ProjectEventType { return ProjectEventType.AssignRandomUsername; }
    public get undoEventTypeMinorVersion(): number { return MINOR_VERSION; }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public createUndoEvent = (_undoEvent: AssignRandomUsername_LocalEvent, _originalEventProjectionResult: AssignRandomUsername_ProjectionResult): boolean => {
        return false;
    }

    public projectOntoWorld = (event: AssignRandomUsername_LocalEvent, addUpdatedEntitiesToDirtySet: boolean, returnProjectionResults: boolean): AssignRandomUsername_ProjectionResult | undefined => {
        const projectuserId = event.userId ?? event.clientId!;
        const entity = this.projectWorld.projectUsers.maybeGet(projectuserId);

        if (entity === undefined) {
            return undefined; // No user found with this ID, cannot assign random username
        }

        // Verify this event is newer than the last processed event of this type
        if (entity.lastEventTime[event.tp] !== undefined && entity.lastEventTime[event.tp] > event.et) {
            return undefined;
        }

        entity.randomUsername = event.randomUsername;

        // Update metadata
        entity.lastEventTime[event.tp] = event.et;
        entity.modifiedByClientId = event.ci;
        entity.modifiedByUserId = event.ui;
        entity.modifiedOn = event.et;


        if (addUpdatedEntitiesToDirtySet) {
            this.projectWorld.addEntityToSave('projectUsers', entity);
        }
        return !returnProjectionResults ? undefined : {
            kind: 'AssignRandomUsername_ProjectionResult',
            projectUser: entity,
        };
    }

    public createLocalEvent = (eventToPopulate: AssignRandomUsername_LocalEvent, userInputs: AssignRandomUsername_EventInputs): boolean => {
        const id = userInputs.userId ?? userInputs.clientId;

        if (!id) {
            log.warn("Invalid ProvideAccess event: missing required fields (userId or clientId)", event);
            return false;
        }

        const entity = this.projectWorld.projectUsers.maybeGet(id);
        if (entity === undefined) {
            log.warn("No user found with this ID, cannot assign random username", id);
            return false; // 
        }

        if (entity.randomUsername !== undefined) {
            log.warn("User already has random username", id, entity.randomUsername);
            return false;
        }

        eventToPopulate.clientId = userInputs.clientId;
        eventToPopulate.userId = userInputs.userId;
        eventToPopulate.randomUsername = userInputs.randomUsername;

        return true;
    }

    public toServerEvent = async (localEvent: AssignRandomUsername_LocalEvent): Promise<ServerEvent<ProjectEventType>> => {
        const eventData = {
            clientId: localEvent.clientId,
            userId: localEvent.userId,
            randomUsername: localEvent.randomUsername
        };
        const eventDataString = JSON.stringify(eventData);
        return createServerEvent(localEvent, eventDataString);
    }

    public fromServerEvent = async (localEventToPopulate: AssignRandomUsername_LocalEvent, serverEvent: ServerEvent<ProjectEventType>): Promise<void> => {
        const eventData = JSON.parse(atob(serverEvent.ev))
        Object.assign(localEventToPopulate, eventData);
    }
}